// TutorialHelper.tsx - A friendly knight guide for new users
import React, { useState, useEffect } from 'react';

// Sprite positions in the 3x3 grid (row, col)
const POSES = {
  wave: { row: 0, col: 0 }, 
  present: { row: 0, col: 1 }, 
  salute: { row: 0, col: 2 }, 
  point: { row: 1, col: 0 }, 
  explain: { row: 1, col: 1 }, 
  confident: { row: 1, col: 2 }, 
  think: { row: 2, col: 0 }, 
  proud: { row: 2, col: 1 }, 
  ready: { row: 2, col: 2 }, 
} as const;

type PoseKey = keyof typeof POSES;

interface TutorialStep {
  id: string;
  title: string;
  message: string;
  pose: PoseKey;
  route?: string;        // Show on specific route/tab
  condition?: string;    // Additional condition key
  priority?: number;     // Higher = show first
}

// Tutorial content - easily expandable!
const TUTORIAL_STEPS: TutorialStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Rallify!',
    message: "Hey there, adventurer! I'm Sir Ralli, your guide to raid scheduling greatness. As you navigate the system, I'll appear with helpful advice!",
    pose: 'wave',
    route: 'Home',
    priority: 100,
  },
  {
    id: 'guilds-intro',
    title: 'Join or Create a Guild',
    message: "Here on the Guilds tab you can join an existing guild with an invite code, or create your own! Every great raid team starts here. To get an invite code reach out to a guild leader",
    pose: 'point',
    route: 'Guilds',
    priority: 90,
  },
  {
    id: 'roster-intro',
    title: 'Add Your Characters',
    message: "The Roster tab is where you add your characters. Just enter your character name and realm - we'll fetch the rest from the armory!",
    pose: 'present',
    route: 'Roster',
    priority: 85,
  },
  {
    id: 'planner-intro',
    title: 'Plan Your Raids',
    message: "This is where the magic happens! Create raids, set times and see your guilds best raid days. You can even schedule raids based on availability preferences!",
    pose: 'explain',
    route: 'Planner',
    priority: 80,
  },
  {
    id: 'find-groups',
    title: 'Find Groups',
    message: "Looking for more? The Find tab shows players and groups looking for members. Great for pugging or recruiting!",
    pose: 'point',
    route: 'Find',
    priority: 75,
  },
  {
    id: 'raid-signup',
    title: 'Signing Up for Raids',
    message: "Click on any raid to see details and sign up with your characters. Mark yourself as Confirmed, Tentative, or Declined!",
    pose: 'confident',
    route: 'Planner',
    condition: 'has-raids',
    priority: 70,
  },
  {
    id: 'availability',
    title: 'Set Your Availability',
    message: "Pro tip: When adding a character, you can set your weekly availability. Raid leaders can use this to find the best times for everyone!",
    pose: 'think',
    route: 'Roster',
    priority: 65,
  },
  {
    id: 'core-complete',
    title: 'Core Steps Done!',
    message: "Great job finishing the core steps! If you aren't a guild or raid leader, you can ignore the remaining tips. Even without a guild, our automatic scheduling will find raids for you!",
    pose: 'proud',
    route: 'Home',
    condition: 'core-steps-complete',
    priority: 60,
  },
  {
    id: 'complete',
    title: "You're All Set!",
    message: "You've got the basics down! If you ever need help, I'll be right here. Now go forth and conquer those raids!",
    pose: 'proud',
    condition: 'all-steps-complete',
    priority: 0,
  },
];

interface TutorialHelperProps {
  currentTab?: string;
  isLoggedIn?: boolean;
  hasGuild?: boolean;
  hasCharacters?: boolean;
  hasRaids?: boolean;
  hasAvailability?: boolean;
  spriteUrl?: string;
}

const STORAGE_KEY = 'rallify_tutorial_progress';

export default function TutorialHelper({
  currentTab = 'Home',
  isLoggedIn = false,
  hasGuild = false,
  hasCharacters = false,
  hasRaids = false,
  hasAvailability = false,
  spriteUrl = '/knight-sprites.png', // Default path - update to your S3 URL
}: TutorialHelperProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [currentStep, setCurrentStep] = useState<TutorialStep | null>(null);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());
  const [hasSeenWelcome, setHasSeenWelcome] = useState(false);
  // Defer to the post-character-creation spotlight tour so we never show two
  // tutorials at once (see OnboardingTour.tsx).
  const [tourActive, setTourActive] = useState<boolean>(() => {
    try { return !!(window as any).__lfmTourActive; } catch { return false; }
  });
  useEffect(() => {
    const h = (e: Event) => setTourActive(!!(e as CustomEvent).detail?.active);
    window.addEventListener("lfm:tour-active", h);
    return () => window.removeEventListener("lfm:tour-active", h);
  }, []);

  // âš¡ï¸ PRELOADER: Fetch the sprite sheet immediately so it's cached and ready to pop!
  useEffect(() => {
    if (spriteUrl) {
      const img = new Image();
      img.src = spriteUrl;
    }
  }, [spriteUrl]);

  // Debug listener for console commands
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail.action === 'show') {
        setCurrentStep(TUTORIAL_STEPS[e.detail.stepIndex] || TUTORIAL_STEPS[0]);
        setIsVisible(true);
      } else if (e.detail.action === 'hide') {
        setIsVisible(false);
      }
    };
    window.addEventListener('tutorial-debug', handler as EventListener);
    return () => window.removeEventListener('tutorial-debug', handler as EventListener);
  }, []);

  // Load progress from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const data = JSON.parse(saved);
        setCompletedSteps(new Set(data.completed || []));
        setHasSeenWelcome(data.hasSeenWelcome || false);
        setIsMinimized(data.isMinimized || false);
      }
    } catch (e) {
      console.warn('Failed to load tutorial progress:', e);
    }
  }, []);

  // Save progress to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        completed: Array.from(completedSteps),
        hasSeenWelcome,
        isMinimized,
      }));
    } catch (e) {
      console.warn('Failed to save tutorial progress:', e);
    }
  }, [completedSteps, hasSeenWelcome, isMinimized]);

  // Find the best step to show based on current context
  useEffect(() => {
    if (!isLoggedIn) {
      setIsVisible(false);
      return;
    }

    // Stand down while the focused setup spotlight tour is running.
    if (tourActive) {
      setIsVisible(false);
      return;
    }

    // Filter steps that match current context and aren't completed
    const availableSteps = TUTORIAL_STEPS.filter(step => {
      // Skip completed steps
      if (completedSteps.has(step.id)) return false;
      
      // Check route match (if specified)
      if (step.route && step.route !== currentTab) return false;
      
      // Check conditions
      if (step.condition === 'has-raids' && !hasRaids) return false;
      if (step.condition === 'has-guild' && !hasGuild) return false;
      if (step.condition === 'has-characters' && !hasCharacters) return false;
      
      // The 'core-complete' step shows when user has actually created a character AND set availability
      if (step.condition === 'core-steps-complete') {
        if (!hasCharacters || !hasAvailability) return false;
      }
      
      // The 'complete' step should only show when ALL other steps are completed
      if (step.condition === 'all-steps-complete') {
        const otherSteps = TUTORIAL_STEPS.filter(s => s.id !== 'complete');
        const allOthersComplete = otherSteps.every(s => completedSteps.has(s.id));
        if (!allOthersComplete) return false;
      }
      
      return true;
    });

    // Sort by priority (highest first)
    availableSteps.sort((a, b) => (b.priority || 0) - (a.priority || 0));

    if (availableSteps.length > 0) {
      setCurrentStep(availableSteps[0]);
      // Small delay before showing for smooth UX
      setTimeout(() => setIsVisible(true), 500);
    } else {
      setIsVisible(false);
      setCurrentStep(null);
    }
  }, [currentTab, isLoggedIn, hasGuild, hasCharacters, hasRaids, hasAvailability, completedSteps, tourActive]);

  const markComplete = () => {
    if (currentStep) {
      setCompletedSteps(prev => new Set([...Array.from(prev), currentStep.id]));
      if (currentStep.id === 'welcome') {
        setHasSeenWelcome(true);
      }
    }
  };

  const dismissAll = () => {
    // Mark all steps as complete
    setCompletedSteps(new Set(TUTORIAL_STEPS.map(s => s.id)));
    setIsVisible(false);
  };

  if (!isVisible || !currentStep) return null;

  const pose = POSES[currentStep.pose];

  // Minimized state - just show the knight peeking
  if (isMinimized) {
    return (
      <div
        className="fixed bottom-4 right-[100px] z-50 cursor-pointer group"
        onClick={() => setIsMinimized(false)}
      >
        <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/20 to-amber-600/10 border-2 border-amber-400/30 shadow-lg overflow-hidden hover:scale-110 transition-transform">
          <div
            className="absolute w-[192px] h-[192px]"
            style={{
              backgroundImage: `url(${spriteUrl})`,
              backgroundSize: '300% 300%',
              backgroundPosition: `${pose.col * 50}% ${pose.row * 50}%`,
              top: '-40px',
              left: '-64px',
            }}
          />
        </div>
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-cyan-500 rounded-full flex items-center justify-center text-[10px] font-bold text-white shadow-lg animate-pulse">
          ?
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed bottom-4 right-24 z-50"
      style={{
        // This cubic-bezier creates the "overshoot" (bounce) effect
        animation: 'tutorialPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both',
        transformOrigin: 'bottom center' 
      }}
    >
      <style>{`
        @keyframes tutorialPop {
          0% { opacity: 0; transform: scale(0.8) translateY(20px); }
          100% { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
      {/* Wide speech bubble with knight inside on the left */}
      <div className="relative flex flex-col bg-gradient-to-br from-[#1a1f2e] to-[#141824] rounded-2xl border border-white/10 shadow-2xl overflow-visible" style={{ width: '480px', minHeight: '160px', height: 'auto' }}>
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 via-amber-500/20 to-cyan-500/20 rounded-2xl blur-xl opacity-50" />
        
        <div className="relative">
          {/* Knight sprite area - anchored to bottom left of bubble, extends above */}
          <div
              className="absolute z-20 pointer-events-none"
              style={{
                left: '-100px', // Moved 10px right (from -100 to -90)
                // This forces everyone down. If some look too low, change -25px to -20px
                bottom: '-25px',
                width: '220px',
                height: '220px',
                backgroundImage: `url(${spriteUrl})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '300% 300%',
                // Back to simple math!
                backgroundPosition: `${POSES[currentStep.pose].col * 50}% ${POSES[currentStep.pose].row * 50}%`,
                filter: 'drop-shadow(2px 2px 4px rgba(0,0,0,0.5))',
              }}
            />

          {/* Vertical border between knight area and text */}
          <div className="absolute top-0 bottom-0 border-l border-amber-400/30" style={{ left: '98px' }} />
          
          {/* Text content - padded left for knight */}
          <div className="flex flex-col h-full" style={{ marginLeft: '98px' }}>
            {/* Header with close/minimize buttons */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10 bg-white/5 rounded-tr-2xl">
              <span className="text-xs font-semibold text-amber-300/80 tracking-wide uppercase">
                Tutorial
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1 text-white/40 hover:text-white/80 transition-colors"
                  title="Minimize"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <button
                  onClick={dismissAll}
                  className="p-1 text-white/40 hover:text-white/80 transition-colors"
                  title="Dismiss all tutorials"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Text content */}
            <div className="p-4 flex-1">
              <h3 className="text-sm font-bold text-white mb-1">
                {currentStep.title}
              </h3>
              <p className="text-xs text-white/70 leading-relaxed">
                {currentStep.message}
              </p>
            </div>

            {/* Action buttons */}
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/10 bg-white/[0.02] rounded-br-2xl">
              <button
                onClick={dismissAll}
                className="text-[10px] text-white/40 hover:text-white/60 transition-colors"
              >
                Skip all tips
              </button>
              <button
                onClick={markComplete}
                className="px-4 py-1.5 rounded-lg bg-cyan-500/20 border border-cyan-500/30 text-cyan-100 text-xs font-medium hover:bg-cyan-500/30 transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export a reset function for debugging/settings
export function resetTutorialProgress() {
  localStorage.removeItem(STORAGE_KEY);
  window.location.reload();
}

// Debug: Expose tutorial controls to window for console testing
if (typeof window !== 'undefined') {
  (window as any).tutorialDebug = {
    show: (stepIndex: number) => {
      const event = new CustomEvent('tutorial-debug', { detail: { action: 'show', stepIndex } });
      window.dispatchEvent(event);
    },
    hide: () => {
      const event = new CustomEvent('tutorial-debug', { detail: { action: 'hide' } });
      window.dispatchEvent(event);
    },
    reset: () => {
      localStorage.removeItem('rallify_tutorial_progress');
      window.location.reload();
    },
    steps: TUTORIAL_STEPS.map((s, i) => `${i}: ${s.id} (${s.pose})`),
  };
}

/*
 * ===========================================
 * TUTORIAL DEBUG COMMANDS (run in browser console)
 * ===========================================
 * 
 * tutorialDebug.steps          // List all available steps
 * tutorialDebug.reset()        // Reset all progress & reload
 * tutorialDebug.hide()         // Hide the tutorial
 * 
 * tutorialDebug.show(0)        // Welcome - wave pose
 * tutorialDebug.show(1)        // Join or Create a Guild - point pose
 * tutorialDebug.show(2)        // Add Your Characters - present pose
 * tutorialDebug.show(3)        // Plan Your Raids - explain pose
 * tutorialDebug.show(4)        // Find Groups - point pose
 * tutorialDebug.show(5)        // Signing Up for Raids - confident pose
 * tutorialDebug.show(6)        // Set Your Availability - think pose
 * tutorialDebug.show(7)        // You're All Set! - proud pose
 * ===========================================
 */